// HR › Assessment Review — the queue of submitted training tests. HR sees the
// score (the taker never does), opens a per-question breakdown, and clicks
// "Verify & Email" to release the result + email it to the taker.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { HRC, fmtDate, Card, SectionTitle, Table, Th, Td, Btn, Modal, Empty, Loading, input } from './hrkit';

const ResultPill = ({ passed }) => (
  <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    color: passed ? HRC.green : HRC.red, background: (passed ? HRC.green : HRC.red) + '1A' }}>
    {passed ? 'PASS' : 'FAIL'}
  </span>
);
const StatusPill = ({ status }) => {
  const c = status === 'released' ? HRC.green : HRC.amber;
  return <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    color: c, background: c + '1A', textTransform: 'capitalize' }}>{status}</span>;
};

export default function AssessmentReview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [department, setDepartment] = useState('');
  const [depts, setDepts] = useState([]);
  const [open, setOpen] = useState(null);      // attempt detail
  const [detailLoading, setDetailLoading] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [flash, setFlash] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (status) params.status = status;
    if (department) params.department = department;
    api.get('/assessment/queue', { params })
      .then(r => setRows(r.data.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [status, department]);
  useEffect(() => { load(); }, [load]);

  // Department filter options (unique, from the loaded rows + a context call).
  useEffect(() => {
    api.get('/assessment/context').then(r => setDepts(r.data.data?.departments || [])).catch(() => {});
  }, []);

  const view = (id) => {
    setDetailLoading(true); setOpen({ id });
    api.get(`/assessment/attempt/${id}`)
      .then(r => setOpen(r.data.data))
      .catch(e => { setFlash(e.response?.data?.error || 'Could not load attempt'); setOpen(null); })
      .finally(() => setDetailLoading(false));
  };

  const release = (id) => {
    setReleasing(true);
    api.post(`/assessment/attempt/${id}/release`)
      .then(r => {
        const d = r.data.data;
        setFlash(d.emailed ? `Released — result emailed to ${d.email}.` : `Released, but email not sent${d.emailError ? ' (' + d.emailError + ')' : ''}.`);
        setOpen(null); load();
      })
      .catch(e => setFlash(e.response?.data?.error || 'Release failed'))
      .finally(() => setReleasing(false));
  };

  return (
    <Card>
      <SectionTitle right={
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={department} onChange={e => setDepartment(e.target.value)} style={{ ...input, width: 'auto' }}>
            <option value="">All departments</option>
            {depts.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
          <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...input, width: 'auto' }}>
            <option value="">All status</option>
            <option value="submitted">Awaiting review</option>
            <option value="released">Released</option>
          </select>
        </div>
      }>Assessment Review</SectionTitle>

      {flash && <div style={{ background: HRC.violet, color: HRC.navy, padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{flash}</div>}

      {loading ? <Loading /> : rows.length === 0 ? <Empty>No assessments submitted yet.</Empty> : (
        <Table>
          <thead><tr>
            <Th>Name</Th><Th>Department</Th><Th>Score</Th><Th>%</Th><Th>Result</Th>
            <Th>Attempt</Th><Th>Date</Th><Th>Status</Th><Th style={{ textAlign: 'right' }}></Th>
          </tr></thead>
          <tbody>
            {rows.map(a => (
              <tr key={a.id}>
                <Td style={{ fontWeight: 600 }}>{a.taker_name || '—'}</Td>
                <Td>{a.department_label}</Td>
                <Td>{a.score}/{a.total}</Td>
                <Td style={{ fontWeight: 700 }}>{a.percent}%</Td>
                <Td><ResultPill passed={a.passed} /></Td>
                <Td>#{a.attempt_no}</Td>
                <Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(a.created_at)}</Td>
                <Td><StatusPill status={a.status} />{a.status === 'released' && !a.emailed && <span title="email not sent" style={{ marginLeft: 6, color: HRC.amber }}>⚠</span>}</Td>
                <Td style={{ textAlign: 'right' }}><Btn size="sm" variant="soft" onClick={() => view(a.id)}>View</Btn></Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {open && (
        <Modal title="Assessment breakdown" width={680} onClose={() => setOpen(null)}>
          {detailLoading || !open.attempt ? <Loading /> : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{open.attempt.taker_name}</div>
                  <div style={{ fontSize: 12, color: HRC.muted }}>{open.attempt.department_label} · attempt #{open.attempt.attempt_no} · {fmtDate(open.attempt.created_at)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: open.attempt.passed ? HRC.green : HRC.red }}>{open.attempt.score}/{open.attempt.total} · {open.attempt.percent}%</div>
                  <div><ResultPill passed={open.attempt.passed} /></div>
                </div>
              </div>

              <div style={{ maxHeight: '52vh', overflowY: 'auto', paddingRight: 4 }}>
                {open.breakdown.map(b => (
                  <div key={b.n} style={{ borderTop: `1px solid ${HRC.border}`, padding: '10px 0' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                      <span style={{ color: b.is_correct ? HRC.green : HRC.red, marginRight: 6 }}>{b.is_correct ? '✓' : '✗'}</span>
                      {b.n}. {b.question}
                    </div>
                    {b.options.map((opt, i) => {
                      const isCorrect = i === b.correct_index;
                      const isChosen = i === b.chosen_index;
                      const bg = isCorrect ? HRC.green + '18' : (isChosen ? HRC.red + '14' : 'transparent');
                      const col = isCorrect ? HRC.green : (isChosen ? HRC.red : HRC.text);
                      return (
                        <div key={i} style={{ fontSize: 12.5, padding: '4px 8px', borderRadius: 6, background: bg, color: col, marginBottom: 2 }}>
                          {opt}
                          {isCorrect && <span style={{ fontWeight: 700, marginLeft: 6 }}>✓ correct</span>}
                          {isChosen && !isCorrect && <span style={{ fontWeight: 700, marginLeft: 6 }}>← their answer</span>}
                        </div>
                      );
                    })}
                    {b.chosen_index == null && <div style={{ fontSize: 11, color: HRC.amber, marginTop: 2 }}>Not answered</div>}
                    {b.explanation && <div style={{ fontSize: 11.5, color: HRC.muted, marginTop: 5, fontStyle: 'italic' }}>{b.explanation}</div>}
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <Btn variant="ghost" onClick={() => setOpen(null)}>Close</Btn>
                {open.attempt.status === 'submitted'
                  ? <Btn onClick={() => release(open.attempt.id)} disabled={releasing}>{releasing ? 'Sending…' : '✓ Verify & Email'}</Btn>
                  : <span style={{ fontSize: 12, color: HRC.green, alignSelf: 'center' }}>Released {open.attempt.emailed ? '· emailed' : '· not emailed'} {fmtDate(open.attempt.released_at)}</span>}
              </div>
            </div>
          )}
        </Modal>
      )}
    </Card>
  );
}
